import { useTheme } from "@emotion/react";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LanguageOutlined,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import api from "../../../../../services/api";
import useStyles from "./styles";

const MACRO_FORMAT_REGEX = /^\{\$[A-Z0-9_]+\}$/i;

const validateMacro = (macro) => {
  const value = (macro || "").trim();
  if (!value) {
    return "O campo Macro é obrigatório";
  }
  if (!MACRO_FORMAT_REGEX.test(value)) {
    return "Formato inválido. Utilize o padrão {$NOME_DA_MACRO}";
  }
  return "";
};

export default function MacrosGlobais() {
  const theme = useTheme();
  const styles = useStyles(theme);

  // Estados
  const [macros, setMacros] = useState([]);
  const [originalMacros, setOriginalMacros] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const macroErrors = useMemo(() => {
    const errors = {};
    macros.forEach((item) => {
      errors[item.globalmacroid] = validateMacro(item.macro);
    });
    return errors;
  }, [macros]);

  const hasInvalidMacro = Object.values(macroErrors).some(Boolean);

  // Busca inicial dos dados
  useEffect(() => {
    fetchMacros();
  }, []);

  const fetchMacros = async () => {
    setLoading(true);
    try {
      const response = await api.get("/interno/getMacrosGlobais");
      setMacros(response.data || []);
      setOriginalMacros(response.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: error.message || "Erro ao buscar macros globais",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Funções de Controle de Estado Local ---

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setMacros(originalMacros);
    setIsEditing(false);
  };

  const handleAddMacro = () => {
    const newMacro = {
      globalmacroid: `temp_${Date.now()}`,
      macro: "{$NOVA_MACRO}",
      value: "",
      description: "",
      type: "0",
      isNew: true,
    };
    setMacros([...macros, newMacro]);
  };

  const handleDeleteMacro = (id) => {
    const updatedMacros = macros.filter((m) => m.globalmacroid !== id);
    setMacros(updatedMacros);
  };

  const handleChange = (id, field, newValue) => {
    const updatedMacros = macros.map((m) => {
      if (m.globalmacroid === id) {
        return { ...m, [field]: newValue };
      }
      return m;
    });
    setMacros(updatedMacros);
  };

  // --- Funções de Persistência (API) ---

  const handleSaveClick = async () => {
    if (hasInvalidMacro) {
      setSnackbar({
        severity: "error",
        children: "Corrija as macros inválidas antes de salvar",
      });
      return;
    }

    setSaving(true);
    try {
      // Prepara o array limpando o ID temporário dos itens novos para o backend
      const payload = macros.map((m) => {
        if (m.isNew) {
          // Extrai o globalmacroid e o isNew, enviando apenas os dados reais
          const { globalmacroid, isNew, ...rest } = m;
          return rest;
        }
        return m;
      });

      // Dispara o POST enviando o array completo como o backend espera
      // OBS: Se o seu backend espera req.body.macros, mude para { macros: payload }
      const response = await api.post("/interno/updateMacrosGlobais", {
        payload: payload,
      });
      setMacros(response.data.macros || []);
      setOriginalMacros(response.data.macros || []);

      setSnackbar({
        severity: "success",
        children: "Macros salvas com sucesso!",
      });

      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: error.message || "Erro ao salvar as macros",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper
        sx={styles.mainPaper}
        elevation={theme.palette.mode === "dark" ? 0 : 3}
      >
        {/* Cabeçalho */}
        <Box sx={styles.header}>
          <LanguageOutlined color="primary" />
          <Typography variant="h5" sx={styles.title}>
            Macros Globais
          </Typography>
        </Box>

        <Paper variant="outlined" sx={styles.macrosContainer}>
          {/* Lista de Macros */}
          <Box display="flex" flexDirection="column" gap={2}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {macros.length === 0 && (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    py={4}
                  >
                    Nenhuma macro configurada.
                  </Typography>
                )}

                {macros.map((item) => (
                  <Box
                    key={item.globalmacroid}
                    display="flex"
                    gap={2}
                    alignItems="flex-start"
                    sx={styles.macroRow}
                  >
                    <TextField
                      label="Macro"
                      fullWidth
                      size="small"
                      variant={isEditing ? "outlined" : "filled"}
                      value={item.macro}
                      onChange={(e) =>
                        handleChange(
                          item.globalmacroid,
                          "macro",
                          e.target.value,
                        )
                      }
                      disabled={!isEditing}
                      error={isEditing && !!macroErrors[item.globalmacroid]}
                      helperText={
                        isEditing ? macroErrors[item.globalmacroid] : ""
                      }
                      placeholder={isEditing ? "{$NOME_DA_MACRO}" : undefined}
                      sx={styles.macroField}
                    />

                    <TextField
                      label="Valor"
                      fullWidth
                      size="small"
                      variant={isEditing ? "outlined" : "filled"}
                      value={item.value}
                      onChange={(e) =>
                        handleChange(
                          item.globalmacroid,
                          "value",
                          e.target.value,
                        )
                      }
                      disabled={!isEditing}
                      sx={styles.macroField}
                    />

                    <TextField
                      label="Descrição"
                      fullWidth
                      size="small"
                      variant={isEditing ? "outlined" : "filled"}
                      value={item.description}
                      onChange={(e) =>
                        handleChange(
                          item.globalmacroid,
                          "description",
                          e.target.value,
                        )
                      }
                      disabled={!isEditing}
                      sx={styles.descriptionField}
                    />

                    {/* Espaço reservado para o botão excluir para manter o alinhamento */}
                    {isEditing && (
                      <Tooltip title="Excluir macro">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteMacro(item.globalmacroid)}
                          sx={styles.deleteButton}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Paper>

        {/* Botão Adicionar Logo Abaixo do Último Campo */}
        {isEditing && (
          <Box display="flex" justifyContent="flex-start" mt={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddMacro}
              sx={styles.addButton}
            >
              Adicionar nova macro
            </Button>
          </Box>
        )}

        <Divider sx={styles.divider} />

        {/* Botões de Ação na Parte Inferior */}
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {!isEditing ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              disabled={loading}
              disableElevation
              sx={styles.actionButton}
            >
              Editar
            </Button>
          ) : (
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelClick}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSaveClick}
                disabled={saving || hasInvalidMacro}
                disableElevation
                sx={styles.actionButton}
              >
                {saving ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Alertas */}
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={snackbar?.severity === "success" ? 6000 : 3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
